import { useMemo, useState } from "react";
import api, { fmtIDR, apiErr } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox, exportRows } from "@/components/TableTools";
import { FakturImport } from "@/components/faktur/FakturImport";
import { FakturLink } from "@/components/faktur/FakturLink";
import { toast } from "sonner";
import { Trash2, Loader2, Download } from "lucide-react";

const MATCH = {
  matched: ["Cocok", "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"],
  diff: ["Selisih", "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"],
  unmatched: ["Belum cocok", "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300"],
};
const STATUS = { approved: "Approved", credited: "Dikreditkan", uncredited: "Tidak dikreditkan", void: "Batal/Diganti" };
const BY = { referensi: "nomor referensi", npwp_ppn: "NPWP + PPN", npwp: "NPWP", nama_ppn: "nama + PPN", manual: "tautan manual" };

function MatchCell({ r, kind, reload }) {
  const [label, cls] = MATCH[r.match_status] || MATCH.unmatched;
  return (
    <div className="space-y-0.5" data-testid={`faktur-match-${r.id}`}>
      <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{label}{r.match_status === "diff" ? ` ${fmtIDR(r.diff)}` : ""}</span>
      {r.match_id ? <div className="text-[10px] text-muted-foreground leading-tight">{r.match_ref || "—"} · {fmtDate(r.match_date)} · PPN {fmtIDR(r.match_ppn)}<br />via {BY[r.match_by]}</div>
        : <div className="text-[10px] text-muted-foreground">{r.match_by === "manual_missing" ? "Dokumen tautan manual di luar masa ±1 bulan / tanpa PPN" : kind === "masukan" ? "Tidak ada transaksi ber-PPN yang sesuai" : "Tidak ada invoice yang sesuai"}</div>}
      <FakturLink r={r} kind={kind} reload={reload} />
    </div>
  );
}

function CountCell({ r, kind, reload }) {
  const v = r.override === null || r.override === undefined ? "auto" : r.override ? "yes" : "no";
  const set = async (nv) => {
    try { await api.put(`/faktur/${r.id}`, { override: nv === "auto" ? null : nv === "yes" }); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const yes = kind === "masukan" ? "Dapat dikreditkan" : "Wajib disetor";
  const no = kind === "masukan" ? "Tidak dapat dikreditkan" : "Tidak disetor penjual";
  return (
    <div className="space-y-1 min-w-[11rem]">
      <Select value={v} onValueChange={set}>
        <SelectTrigger className={`h-8 text-xs ${r.counted ? "text-emerald-700" : "text-rose-600"}`} data-testid={`faktur-count-${r.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">Otomatis: {r.auto ? yes : no}</SelectItem>
          <SelectItem value="yes">{yes}</SelectItem>
          <SelectItem value="no">{no}</SelectItem>
        </SelectContent>
      </Select>
      <div className="text-[10px] text-muted-foreground leading-tight">{r.override === null || r.override === undefined ? r.reason : "Diubah manual"}</div>
    </div>
  );
}

function NoFaktur({ kind, docs, period }) {
  if (!docs?.length) return null;
  const cols = [["Tanggal", (d) => fmtDate(d.date)], ["Referensi", "ref"], [kind === "masukan" ? "Vendor" : "Pelanggan", "party"], ["PPN", "ppn"]];
  return (
    <Card className="p-4 space-y-2" data-testid={`faktur-nofaktur-${kind}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div><h3 className="font-head font-semibold text-sm">{kind === "masukan" ? "Transaksi ber-PPN tanpa Faktur Masukan" : "Invoice ber-PPN tanpa Faktur Keluaran"} ({docs.length})</h3>
          <p className="text-[11px] text-muted-foreground">PPN di pembukuan yang belum ada fakturnya di Coretax pada masa ini.</p></div>
        <Button variant="outline" size="sm" onClick={() => exportRows(docs, cols, `tanpa_faktur_${kind}_${period}`)} data-testid={`faktur-nofaktur-export-${kind}`}><Download className="h-4 w-4 mr-1" />Ekspor</Button>
      </div>
      <div className="overflow-auto rounded-md border border-border max-h-72">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50"><TableHead>Tanggal</TableHead><TableHead>Referensi</TableHead><TableHead>{cols[2][0]}</TableHead><TableHead className="text-right">PPN</TableHead></TableRow></TableHeader>
          <TableBody>{docs.map((d) => (
            <TableRow key={d.id} data-testid={`faktur-nofaktur-row-${d.id}`}><TableCell className="num text-xs">{fmtDate(d.date)}</TableCell><TableCell className="text-xs">{d.ref || "—"}</TableCell>
              <TableCell className="text-xs">{d.party || "—"}</TableCell><TableCell className="num text-right text-xs">{fmtIDR(d.ppn)}</TableCell></TableRow>))}</TableBody>
        </Table>
      </div>
    </Card>
  );
}

const Filter = ({ value, onChange, tid, items }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-40 text-xs" data-testid={tid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export function FakturTable({ kind, clientId, data, loading, reload, period }) {
  const [mf, setMf] = useState("all");
  const [cf, setCf] = useState("all");
  const all = data?.rows;
  const rows = useMemo(() => (all || []).filter((r) => (mf === "all" || r.match_status === mf) && (cf === "all" || String(r.counted) === cf)), [all, mf, cf]);
  const tools = useTableTools(rows, { date: (r) => r.tanggal, text: (r) => `${r.nomor_faktur} ${r.nama} ${r.npwp} ${r.referensi} ${r.kode} ${r.match_ref || ""} ${r.reason}` });
  const yes = kind === "masukan" ? "Dapat dikreditkan" : "Wajib disetor";
  const cols = [["Nomor Faktur", "nomor_faktur"], ["Tanggal", (r) => fmtDate(r.tanggal)], ["Masa", "masa"], ["Tahun", "tahun"], ["NPWP", "npwp"], ["Nama", "nama"],
    ["Kode", "kode"], ["Keterangan Kode", "kode_label"], ["Status", (r) => STATUS[r.status] || r.status_raw], ["DPP", "dpp"], ["DPP Nilai Lain", "dpp_lain"], ["PPN", "ppn"], ["PPnBM", "ppnbm"],
    ["Referensi", "referensi"], ["Hasil Cocok", (r) => MATCH[r.match_status]?.[0]], ["Dokumen Pembukuan", (r) => r.match_ref || ""], ["PPN Pembukuan", (r) => r.match_ppn ?? ""],
    ["Selisih", (r) => r.diff ?? ""], [yes, (r) => (r.counted ? "Ya" : "Tidak")], ["Alasan", (r) => (r.override === null || r.override === undefined ? r.reason : "Diubah manual")]];
  const delMany = async (ids) => {
    try { await api.post("/faktur/bulk-delete", { client_id: clientId, ids }); reload(); return { failed: 0 }; }
    catch (e) { return { failed: ids.length, message: apiErr(e.response?.data?.detail) }; }
  };
  const delOne = async (r) => {
    if (!window.confirm(`Hapus faktur ${r.nomor_faktur}?`)) return;
    try { await api.delete(`/faktur/${r.id}`); toast.success("Faktur dihapus"); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const id = `faktur-${kind}`;
  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3" data-testid={`${id}-panel`}>
        <div className="flex flex-wrap items-center gap-2">
          <TableToolbar tools={tools} id={id} cols={cols} filename={`faktur_${kind}_${period}`} onDelete={delMany}>
            <Filter value={mf} onChange={setMf} tid={`${id}-filter-match`} items={[["all", "Semua kecocokan"], ["matched", "Cocok"], ["diff", "Selisih"], ["unmatched", "Belum cocok"]]} />
            <Filter value={cf} onChange={setCf} tid={`${id}-filter-count`} items={[["all", kind === "masukan" ? "Semua kredit" : "Semua setoran"], ["true", yes], ["false", kind === "masukan" ? "Tidak dapat dikreditkan" : "Tidak disetor"]]} />
            <FakturImport kind={kind} clientId={clientId} onDone={reload} />
          </TableToolbar>
        </div>
        <div className="overflow-auto rounded-md border border-border">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50">
              <TableHead className="w-8"><SelectAllBox tools={tools} id={id} /></TableHead>
              <TableHead>No. Faktur / Tanggal</TableHead><TableHead>{kind === "masukan" ? "Penjual" : "Pembeli"}</TableHead><TableHead>Kode</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">DPP</TableHead><TableHead className="text-right">PPN</TableHead><TableHead>Pencocokan</TableHead>
              <TableHead>{kind === "masukan" ? "Pengkreditan" : "Setoran"}</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && !all && <TableRow><TableCell colSpan={10} className="py-8 text-center"><Loader2 className="inline h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {all && tools.view.length === 0 && <TableRow><TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground">Belum ada faktur {kind} pada masa ini. Klik "Impor Coretax" untuk mengunggah file ekspor.</TableCell></TableRow>}
              {tools.view.map((r) => (
                <TableRow key={r.id} data-testid={`${id}-row-${r.id}`} className={r.status === "void" ? "opacity-60" : ""}>
                  <TableCell><SelectBox tools={tools} row={r} id={id} /></TableCell>
                  <TableCell className="whitespace-nowrap"><div className="num text-xs font-medium">{r.nomor_faktur}</div><div className="text-[10px] text-muted-foreground">{fmtDate(r.tanggal)} · masa {r.masa}/{r.tahun}{r.referensi ? ` · ${r.referensi}` : ""}</div></TableCell>
                  <TableCell className="text-xs"><div className="font-medium">{r.nama || "—"}</div><div className="num text-[10px] text-muted-foreground">{r.npwp || "tanpa NPWP"}</div></TableCell>
                  <TableCell><Badge variant="outline" className="num text-[10px]" title={r.kode_label}>{r.kode}</Badge></TableCell>
                  <TableCell className="text-[11px] whitespace-nowrap">{STATUS[r.status] || r.status_raw}</TableCell>
                  <TableCell className="num text-right text-xs">{fmtIDR(r.dpp)}</TableCell>
                  <TableCell className="num text-right text-xs font-semibold">{fmtIDR(r.ppn)}</TableCell>
                  <TableCell><MatchCell r={r} kind={kind} reload={reload} /></TableCell>
                  <TableCell><CountCell r={r} kind={kind} reload={reload} /></TableCell>
                  <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => delOne(r)} data-testid={`${id}-del-${r.id}`}><Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      <NoFaktur kind={kind} docs={data?.docs_without_faktur} period={period} />
    </div>
  );
}
